import { useCallback, useEffect, useState } from 'react';
import type { TimeOffDto } from '../../api/cabinetTypes';
import { useAppApi } from '../../app/ApiProvider';
import { dayKeyInTz, formatDateTimeInTz, shiftDayKey, type DayKey } from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import type { UiKey } from '../../i18n/dict';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { Chip } from '../../ui/controls';
import { CtaBar, CtaButton, SmallButton } from '../../ui/Cta';
import { EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, Section, SectionTitle, TopBar } from '../../ui/Screen';
import { Field, FieldPair, FormError, Note, Row, TextInput, Toggle } from './settings/parts';
import { parseHhMm } from './settings/week';
import { endOfDayIso, timeOffRange, zonedIso } from './settings/zoned';
import { useBusiness } from './useBusiness';

/** Глубина окна просмотра по умолчанию. Два месяца вперёд — горизонт, на который планируют отпуск. */
const DEFAULT_WINDOW_DAYS = 60;

/**
 * «Отпуска и отсутствия» кабинета (ТЗ §14.1).
 *
 * ОТПУСК — АБСОЛЮТНЫЙ ИНТЕРВАЛ С ДАТАМИ, в отличие от рабочих часов, у которых есть только
 * день недели. Разница не техническая: закрыть один конкретный день правкой часов нельзя —
 * закроется этот день недели навсегда, включая все следующие недели. Об этом сказано текстом
 * (`offNote`), потому что снаружи оба экрана выглядят как «расписание».
 *
 * ГРАНИЦЫ УХОДЯТ ISO-СТРОКОЙ СО СМЕЩЕНИЕМ ПОЯСА ОРГАНИЗАЦИИ (см. `settings/zoned.ts`).
 * Строка без зоны трактовалась бы в поясе сервера, а смещение телефона закрыло бы
 * ташкентскому мастеру день начиная с 02:00 (AGENTS.md, правило 1).
 *
 * Список требует и мастера, и периода — оба параметра обязательны в API, и «показать все
 * отсутствия» такого запроса просто нет.
 */
export function TimeOffScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();
  const { businessId } = props;

  const businessLoad = useBusiness(businessId);
  const staffList = useAsync(
    useCallback(() => api.cabinet.listStaff(businessId), [api, businessId]),
    [businessId],
  );

  const business = businessLoad.business;
  const tz = business?.tz ?? null;

  const [staffId, setStaffId] = useState<string | null>(null);
  const [fromDay, setFromDay] = useState<DayKey | null>(null);
  const [toDay, setToDay] = useState<DayKey | null>(null);

  const [form, setForm] = useState<TimeOffForm | null>(null);
  const [formProblem, setFormProblem] = useState<UiKey | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<TimeOffDto | null>(null);
  const [removeError, setRemoveError] = useState<unknown>(null);

  // Период считается в поясе организации: «сегодня» у ташкентского салона и у телефона
  // в другом поясе — разные дни, и окно просмотра съехало бы на сутки.
  useEffect(() => {
    if (!tz) return;
    const today = dayKeyInTz(new Date(), tz);
    setFromDay((current) => current ?? today);
    setToDay((current) => current ?? shiftDayKey(today, DEFAULT_WINDOW_DAYS));
  }, [tz]);

  const staffState = staffList.state;
  useEffect(() => {
    if (staffState.status !== 'ready') return;
    setStaffId((current) =>
      current && staffState.data.some((member) => member.id === current)
        ? current
        : (staffState.data[0]?.id ?? null),
    );
  }, [staffState]);

  const list = useAsync(
    useCallback(async () => {
      if (!tz || !staffId || !fromDay || !toDay) return null;
      return api.cabinet.listTimeOff(businessId, {
        staffId,
        from: zonedIso(fromDay, 0, tz),
        // Правая граница — полночь следующих суток: иначе последний день периода
        // попадал бы в выборку только до 00:00, то есть не попадал бы вовсе.
        to: endOfDayIso(toDay, tz),
      });
    }, [api, businessId, staffId, fromDay, toDay, tz]),
    [businessId, staffId, fromDay, toDay, tz],
  );

  const submit = async (): Promise<void> => {
    if (!form || !tz || busy) return;

    if (!form.staffId) {
      setFormProblem('offErrStaff');
      return;
    }

    const fromMin = form.allDay ? 0 : parseHhMm(form.fromTime);
    const toMin = form.allDay ? 0 : parseHhMm(form.toTime);
    if (fromMin === null || toMin === null) {
      setFormProblem('offErrTime');
      return;
    }

    const range = timeOffRange(
      { fromDay: form.fromDay, toDay: form.toDay, allDay: form.allDay, fromMin, toMin },
      tz,
    );
    if (!range) {
      setFormProblem('offErrRange');
      return;
    }

    setBusy(true);
    setFormProblem(null);
    setSaveError(null);
    try {
      const reason = form.reason.trim();
      await api.cabinet.addTimeOff(businessId, {
        staffId: form.staffId,
        startsAt: range.startsAt,
        endsAt: range.endsAt,
        ...(reason ? { reason } : {}),
      });
      // Отсутствие ставится выбранному в форме мастеру, а список показывает выбранного
      // в фильтре. Переключаем фильтр, иначе только что созданная запись не появится
      // на экране, и владелец создаст её второй раз.
      setStaffId(form.staffId);
      setForm(null);
      list.reload();
    } catch (error) {
      setSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!removing || busy) return;
    setBusy(true);
    setRemoveError(null);
    try {
      await api.cabinet.removeTimeOff(businessId, removing.id);
      setRemoving(null);
      list.reload();
    } catch (error) {
      setRemoveError(error);
    } finally {
      setBusy(false);
    }
  };

  if (businessLoad.state.status === 'failed') {
    return (
      <Screen>
        <TopBar title={t('offTitle')} onBack={props.onBack} />
        <ErrorState error={businessLoad.state.error} onRetry={businessLoad.reload} />
      </Screen>
    );
  }

  if (staffList.state.status === 'failed') {
    return (
      <Screen>
        <TopBar title={t('offTitle')} onBack={props.onBack} />
        <ErrorState error={staffList.state.error} onRetry={staffList.reload} />
      </Screen>
    );
  }

  if (!business || staffList.state.status !== 'ready' || !fromDay || !toDay) {
    return (
      <Screen>
        <TopBar title={t('offTitle')} onBack={props.onBack} />
        <Loader />
      </Screen>
    );
  }

  const staff = staffList.state.data;

  return (
    <Screen>
      <TopBar title={t('offTitle')} subtitle={business.name} onBack={props.onBack} />

      <ScrollArea bottomGap="cta">
        <Section>
          <SectionTitle>{t('offStaff')}</SectionTitle>
          {staff.length === 0 ? (
            <EmptyState>{t('offNoStaff')}</EmptyState>
          ) : (
            <div className="no-scrollbar flex gap-2 overflow-x-auto py-0.5">
              {staff.map((member) => (
                <Chip
                  key={member.id}
                  active={member.id === staffId}
                  onClick={() => setStaffId(member.id)}
                >
                  {member.name}
                </Chip>
              ))}
            </div>
          )}
        </Section>

        <Section>
          <SectionTitle>{t('offPeriod')}</SectionTitle>
          <FieldPair>
            <Field label={t('offFrom')}>
              <TextInput type="date" value={fromDay} onChange={setFromDay} />
            </Field>
            <Field label={t('offTo')}>
              <TextInput type="date" value={toDay} onChange={setToDay} />
            </Field>
          </FieldPair>
        </Section>

        <Section>
          {staff.length === 0 ? null : list.state.status === 'loading' ? (
            <Loader />
          ) : list.state.status === 'failed' ? (
            <ErrorState error={list.state.error} onRetry={list.reload} />
          ) : list.state.data && list.state.data.length > 0 ? (
            list.state.data.map((item) => (
              <Row key={item.id}>
                <div className="text-[14px] font-semibold">
                  {formatDateTimeInTz(item.startsAt, business.tz, lang)} —{' '}
                  {formatDateTimeInTz(item.endsAt, business.tz, lang)}
                </div>
                {item.reason ? (
                  <div className="mt-0.5 text-[12px] text-ink-3">{item.reason}</div>
                ) : null}
                <div className="mt-2.5 flex">
                  <SmallButton variant="warn" disabled={busy} onClick={() => setRemoving(item)}>
                    {t('offRemove')}
                  </SmallButton>
                </div>
              </Row>
            ))
          ) : (
            <EmptyState>{t('offEmpty')}</EmptyState>
          )}
          <Note>{t('offNote')}</Note>
        </Section>
      </ScrollArea>

      <CtaBar>
        <CtaButton
          disabled={staff.length === 0}
          onClick={() => {
            setFormProblem(null);
            setSaveError(null);
            setForm({
              staffId: staffId ?? staff[0]?.id ?? null,
              fromDay,
              toDay: fromDay,
              allDay: true,
              fromTime: '10:00',
              toTime: '18:00',
              reason: '',
            });
          }}
        >
          {t('offAdd')}
        </CtaButton>
      </CtaBar>

      <BottomSheet open={form !== null} title={t('offAdd')} onClose={() => setForm(null)}>
        {form ? (
          <>
            <Field label={t('offStaff')}>
              <div className="no-scrollbar flex gap-2 overflow-x-auto py-0.5">
                {staff.map((member) => (
                  <Chip
                    key={member.id}
                    active={member.id === form.staffId}
                    onClick={() => setForm({ ...form, staffId: member.id })}
                  >
                    {member.name}
                  </Chip>
                ))}
              </div>
            </Field>

            {/* Весь день — это [00:00, 00:00 следующих суток), а не «до 23:59»: занятость
                сервер сравнивает полуинтервалами, и последняя минута осталась бы открытой. */}
            <Toggle
              on={form.allDay}
              onChange={(on) => setForm({ ...form, allDay: on })}
              label={t('offAllDay')}
            />

            <FieldPair>
              <Field label={t('offFrom')}>
                <TextInput
                  type="date"
                  value={form.fromDay}
                  onChange={(value) => setForm({ ...form, fromDay: value })}
                />
              </Field>
              {form.allDay ? null : (
                <Field label={t('offTime')}>
                  <TextInput
                    value={form.fromTime}
                    onChange={(value) => setForm({ ...form, fromTime: value })}
                    placeholder="10:00"
                  />
                </Field>
              )}
            </FieldPair>

            <FieldPair>
              <Field label={t('offTo')}>
                <TextInput
                  type="date"
                  value={form.toDay}
                  onChange={(value) => setForm({ ...form, toDay: value })}
                />
              </Field>
              {form.allDay ? null : (
                <Field label={t('offTime')}>
                  <TextInput
                    value={form.toTime}
                    onChange={(value) => setForm({ ...form, toTime: value })}
                    placeholder="18:00"
                  />
                </Field>
              )}
            </FieldPair>

            <Field label={t('offReason')}>
              <TextInput
                value={form.reason}
                onChange={(value) => setForm({ ...form, reason: value })}
                placeholder={t('offReasonPh')}
              />
            </Field>

            {formProblem ? <FormError text={t(formProblem)} /> : null}
            {saveError ? <ErrorState error={saveError} withTitle={false} /> : null}
            <Note>{t('offNote')}</Note>

            <CtaButton onClick={() => void submit()} disabled={busy}>
              {t('itemSave')}
            </CtaButton>
            <CtaButton variant="ghost" onClick={() => setForm(null)} className="mt-2">
              {t('itemCancel')}
            </CtaButton>
          </>
        ) : null}
      </BottomSheet>

      <BottomSheet
        open={removing !== null}
        title={t('offRemoveT')}
        onClose={() => {
          setRemoving(null);
          setRemoveError(null);
        }}
      >
        <div className="mb-3 text-[13px] leading-[1.5] text-ink-2">{t('offRemoveB')}</div>
        {removeError ? <ErrorState error={removeError} withTitle={false} /> : null}
        <CtaButton variant="danger" onClick={() => void remove()} disabled={busy}>
          {t('offRemove')}
        </CtaButton>
        <CtaButton
          variant="ghost"
          className="mt-2"
          onClick={() => {
            setRemoving(null);
            setRemoveError(null);
          }}
        >
          {t('itemCancel')}
        </CtaButton>
      </BottomSheet>
    </Screen>
  );
}

interface TimeOffForm {
  readonly staffId: string | null;
  readonly fromDay: DayKey;
  readonly toDay: DayKey;
  readonly allDay: boolean;
  readonly fromTime: string;
  readonly toTime: string;
  readonly reason: string;
}
